import React, { useEffect, useState } from "react";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { asArray, formatDate, unwrapData } from "../lib/catalog";

function notificationBody(row) {
  const text = String(row.body || row.message || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (/stack(?:trace)?|sqlstate|sequelize|econnrefused|internal server|status code|typeerror|query failed/i.test(text)) {
    return "You have a new update.";
  }
  if (text.length <= 90) return text;
  return `${text.slice(0, 87).trim()}...`;
}

export default function NotificationsPage() {
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState({ email: true, in_app: true });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [loading, setLoading] = useState(true);

  async function loadNotifications() {
    const payload = await agentApi.get("/travel-partner/notifications", {
      params: { page: 1, limit: 50 },
    });
    const data = unwrapData(payload);
    setRows(asArray(data));
    setUnreadCount(Number(data.unreadCount || 0));
  }

  async function loadPreferences() {
    const payload = await agentApi.get("/travel-partner/notifications/preferences");
    const data = unwrapData(payload);
    const prefs = data.preferences || data || {};
    setPreferences({
      email: prefs.email !== false,
      in_app: prefs.in_app !== false,
      whatsapp: Boolean(prefs.whatsapp),
      sms: Boolean(prefs.sms),
      push: Boolean(prefs.push),
    });
  }

  useEffect(() => {
    let alive = true;
    Promise.all([loadNotifications(), loadPreferences()])
      .catch((fetchError) => {
        if (alive) showApiError(fetchError, "LOAD_NOTIFICATIONS");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function savePreferences(next) {
    try {
      setSavingPrefs(true);
      setPreferences(next);
      await agentApi.put("/travel-partner/notifications/preferences", {
        body: {
          email_enabled: next.email,
          in_app_enabled: next.in_app,
          whatsapp_enabled: Boolean(next.whatsapp),
          sms_enabled: Boolean(next.sms),
          push_enabled: Boolean(next.push),
        },
      });
      showSuccessAction("SAVE_NOTIFICATION_PREFERENCES");
    } catch (saveError) {
      showApiError(saveError, "SAVE_NOTIFICATION_PREFERENCES");
    } finally {
      setSavingPrefs(false);
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Notifications"
          title="All Notifications"
          description={
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
              <StatusBadge value={unreadCount > 0 ? "unread" : "read"}>
                {unreadCount} unread
              </StatusBadge>
            </div>
          }
          items={[DASHBOARD_CRUMB, { label: "Notifications" }]}
          actions={
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={async () => {
                try {
                  await agentApi.post("/travel-partner/notifications/read-all", { body: {} });
                  await loadNotifications();
                  showSuccessAction("MARK_ALL_NOTIFICATIONS_READ");
                } catch (markError) {
                  showApiError(markError, "MARK_ALL_NOTIFICATIONS_READ");
                }
              }}
            >
              Mark all read
            </button>
          }
        />

        {loading ? <PageLoader message="Loading notifications..." /> : null}

        {!loading ? (
          <>
            {/* <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-24">
                <h5 className="mb-3">Notification settings</h5>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notify-in-app"
                    checked={preferences.in_app}
                    disabled={savingPrefs}
                    onChange={(event) => savePreferences({ ...preferences, in_app: event.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="notify-in-app">In-app notifications</label>
                </div>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="notify-email"
                    checked={preferences.email}
                    disabled={savingPrefs}
                    onChange={(event) => savePreferences({ ...preferences, email: event.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="notify-email">Email notifications</label>
                </div>
                <p className="small text-secondary mb-0">WhatsApp, SMS, and push alerts are not available yet.</p>
              </div>
            </div> */}

            <div className="card border-0 shadow-sm">
              <div className="card-body p-24">
                {rows.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {rows.map((row) => (
                      <button
                        type="button"
                        className={`agent-notification-item w-100 ${row.read_at ? "" : "is-unread"}`}
                        key={row.notification_id}
                        onClick={async () => {
                          try {
                            await agentApi.post(`/travel-partner/notifications/${row.notification_id}/read`, { body: {} });
                            await loadNotifications();
                            showSuccessAction("MARK_NOTIFICATION_READ");
                          } catch (markError) {
                            showApiError(markError, "MARK_NOTIFICATION_READ");
                          }
                        }}
                      >
                        <div className="w-40-px h-40-px rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0">
                          <iconify-icon icon="solar:bell-outline"></iconify-icon>
                        </div>
                        <div className="flex-grow-1 min-w-0 text-start">
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-1">
                            <div className="fw-semibold min-w-0 text-break">{row.subject || row.event_type || "Notification"}</div>
                            {row.read_at ? <StatusBadge value="read">Read</StatusBadge> : <StatusBadge value="unread">Unread</StatusBadge>}
                          </div>
                          <div className="small text-secondary text-break">{notificationBody(row)}</div>
                          <div className="small text-secondary mt-2">{formatDate(row.created_date)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-secondary text-center py-4">
                    No notifications yet.
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
